import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, mediaUrl } from "@/lib/api";
import { Field, FileField, inputCls } from "./fields";

const EMPTY = { title: "", cover_url: "", blurb: "", release_date: "", spotify_url: "", apple_url: "", order: 0 };

export default function Albums() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/albums").then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/albums/${editing.id}`, editing);
      else await api.post("/admin/albums", editing);
      toast.success("Album saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this album? Tracks stay as singles.")) return;
    await api.delete(`/admin/albums/${id}`);
    load();
  };

  return (
    <div data-testid="admin-albums">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl text-slate-50">Albums</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-gold !px-5 !py-2 text-xs" data-testid="album-new-btn">
          <Plus size={14} /> New Album
        </button>
      </div>
      {editing && (
        <div className="rounded-2xl border border-gold-400/25 bg-navy-900/70 p-6 mb-8 space-y-4" data-testid="album-form">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-lg text-slate-100">{editing.id ? "Edit Album" : "New Album"}</h2>
            <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-200"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Title"><input className={inputCls} value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="album-title-input" /></Field>
            <Field label="Release date"><input type="date" className={inputCls} value={editing.release_date}
              onChange={(e) => setEditing({ ...editing, release_date: e.target.value })} data-testid="album-date-input" /></Field>
          </div>
          <Field label="Blurb"><textarea rows={3} className={inputCls} value={editing.blurb}
            onChange={(e) => setEditing({ ...editing, blurb: e.target.value })} data-testid="album-blurb-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Spotify album link"><input className={inputCls} value={editing.spotify_url} placeholder="https://open.spotify.com/album/…"
              onChange={(e) => setEditing({ ...editing, spotify_url: e.target.value })} data-testid="album-spotify-input" /></Field>
            <Field label="Apple Music album link"><input className={inputCls} value={editing.apple_url} placeholder="https://music.apple.com/…"
              onChange={(e) => setEditing({ ...editing, apple_url: e.target.value })} data-testid="album-apple-input" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <FileField label="Cover art" accept="image/*" value={editing.cover_url}
              onChange={(url) => setEditing({ ...editing, cover_url: url })} testid="album-cover-upload" />
            <Field label="Display order"><input type="number" className={inputCls} value={editing.order}
              onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} data-testid="album-order-input" /></Field>
          </div>
          <button onClick={save} className="btn-gold !px-6 !py-2.5 text-xs" data-testid="album-save-btn">Save Album</button>
        </div>
      )}
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className="flex items-center gap-4 rounded-xl border border-white/5 bg-navy-900/50 px-4 py-3" data-testid={`album-row-${a.id}`}>
            {a.cover_url && <img src={mediaUrl(a.cover_url)} alt="" className="w-10 h-10 rounded-md object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-100 truncate">{a.title}</p>
              <p className="text-[11px] text-slate-500">{a.release_date || "No date"}</p>
            </div>
            <button onClick={() => setEditing({ ...EMPTY, ...a })} className="text-slate-400 hover:text-gold-300 p-2" data-testid={`album-edit-${a.id}`}><Pencil size={15} /></button>
            <button onClick={() => del(a.id)} className="text-slate-500 hover:text-red-400 p-2" data-testid={`album-delete-${a.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-500">No albums yet.</p>}
      </div>
    </div>
  );
}
